/**
 * MobX store for Portal Edge non-font customization: colors, backgrounds, S2E layout, radii, and CSS.
 * Font selection and uploads live in CustomizationEdgeFontsStore.
 */

import { makeAutoObservable, runInAction } from 'mobx';
import { rootContainer, TYPE } from '../../services/container/root';
import {
    IEdgeCustomization,
    PORTAL_BACKGROUND_SIZE,
    PORTAL_BACKGROUND_TYPE,
    S2E_LAYOUT_TYPE,
} from '../../models/User';
import CustomizationsService from '../../services/api/customizations-service';
import {IChangeable, ISaveable, WithOld} from '../types';
import { INewBackgroundImageData, S2ELayoutItemStore } from './s2e-layout-item-store';


interface IEdgeCustomizationChanges extends Partial<IEdgeCustomization> {
    _return_portal_new_background_image?: INewBackgroundImageData;
    _return_portal_delete_background_image?: boolean;
    _return_portal_new_cover_image?: INewBackgroundImageData;
    _return_portal_delete_cover_image?: boolean;
}


export class CustomizationEdgeStore implements ISaveable, IChangeable<IEdgeCustomization> {
    private _changes: IEdgeCustomizationChanges = {};
    private _model?: IEdgeCustomization;
    private _isSaving: boolean = false;

    private _return_portal_background_image_dimensions?: string;
    private _return_portal_cover_image_dimensions?: string;

    private _s2e_layout_data: S2ELayoutItemStore[] = [];

    constructor() {
        makeAutoObservable(this, {}, { autoBind: true });
    }

    private get service(): CustomizationsService {
        return rootContainer.get<CustomizationsService>(TYPE.CustomizationsService);
    }

    public isSaving(): boolean {
        return this._isSaving;
    }

    public getModelForSaveAudit(): IEdgeCustomization | undefined {
        return this._model;
    }

    public setModel(model: IEdgeCustomization): void {
        this._model = {
            ...model,
            show_compare_at_price_strike_through: Boolean(model.show_compare_at_price_strike_through),
            show_product_exchange_price_difference_strike_through: Boolean(model.show_product_exchange_price_difference_strike_through),
        };

        this._s2e_layout_data = [];

        Array.from({ length: 4 }, (_, i) => i + 1).forEach((_, i) => {
            this._s2e_layout_data.push(
              new S2ELayoutItemStore(this._model?.s2e_layout_data?.[i] ?? {})
            )
        });

        this.discardChanges();
        if (this.returnPortalBackgroundType === PORTAL_BACKGROUND_TYPE.IMAGE && this.returnPortalBackgroundValue) {
            this._setReturnPortalBackgroundImageOriginalDimensions();
        }
    }

    /**
     * Merges font-related fields from a server payload (e.g. after multipart font upload) without discarding local edits.
     */
    public mergeServerFontState(server: IEdgeCustomization): void {
        if (!this._model) {
            return;
        }
        runInAction(() => {
            this._model = {
                ...this._model!,
                font: server.font,
                font_slots: server.font_slots,
            };
        });
    }

    public get inputTextColor(): string|undefined { return this._changes.input_text_color ?? this._model?.input_text_color; }
    public get inputBackgroundColor(): string|undefined { return this._changes.input_background_color ?? this._model?.input_background_color; }
    public get buttonBackgroundColor(): string|undefined { return this._changes.button_background_color ?? this._model?.button_background_color; }
    public get buttonTextColor(): string|undefined { return this._changes.button_text_color ?? this._model?.button_text_color; }
    public get textColor(): string|undefined { return this._changes.text_color ?? this._model?.text_color; }
    public get titleColor(): string|undefined { return this._changes.title_color ?? this._model?.title_color; }
    public get errorColor(): string|undefined { return this._changes.error_color ?? this._model?.error_color; }
    public get successColor(): string|undefined { return this._changes.success_color ?? this._model?.success_color; }
    public get portalSecondaryBackgroundColor(): string|undefined { return this._changes.portal_secondary_background_color ?? this._model?.portal_secondary_background_color; }
    public get portalSecondaryBackgroundOpacity(): number|undefined { return this._changes.portal_secondary_background_opacity ?? this._model?.portal_secondary_background_opacity; }
    public get strokeColor(): string|undefined { return this._changes.stroke_color ?? this._model?.stroke_color; }
    public get popupOverlayColor(): string|undefined { return this._changes.popup_overlay_color ?? this._model?.popup_overlay_color; }
    public get popupOverlayOpacity(): number|undefined { return this._changes.popup_overlay_opacity ?? this._model?.popup_overlay_opacity; }
    public get dropShadowColor(): string|undefined { return this._changes.drop_shadow_color ?? this._model?.drop_shadow_color; }
    public get dropShadowOpacity(): number|undefined { return this._changes.drop_shadow_opacity ?? this._model?.drop_shadow_opacity; }
    public get portalCss(): string|undefined { return this._changes.portal_css ?? this._model?.portal_css; }
    public get portalBorderRadius(): number|undefined { return this._changes.portal_border_radius ?? this._model?.portal_border_radius; }
    public get buttonBorderRadius(): number|undefined { return this._changes.button_border_radius ?? this._model?.button_border_radius; }
    public get elementsBorderRadius(): number|undefined { return this._changes.elements_border_radius ?? this._model?.elements_border_radius; }
    public get showCompareAtPriceStrikeThrough(): boolean {
        return this._changes.show_compare_at_price_strike_through ?? this._model?.show_compare_at_price_strike_through ?? false;
    }
    public get showProductExchangePriceDifferenceStrikeThrough(): boolean {
        return (
            this._changes.show_product_exchange_price_difference_strike_through ??
            this._model?.show_product_exchange_price_difference_strike_through ??
            false
        );
    }
    public get returnPortalBackgroundType(): PORTAL_BACKGROUND_TYPE|undefined { return this._changes.return_portal_background_type ?? this._model?.return_portal_background_type; }
    public get returnPortalBackgroundValue(): string | undefined {
        if (this.returnPortalBackgroundType === PORTAL_BACKGROUND_TYPE.IMAGE) {
            if (this._changes._return_portal_delete_background_image) {
                return undefined;
            }
            if (this._changes._return_portal_new_background_image) {
                return this._getNewBackgroundImageBase64Str(this._changes._return_portal_new_background_image)
            } else if (this._model?.return_portal_background_type === PORTAL_BACKGROUND_TYPE.IMAGE) {
                return this._model?.return_portal_background_value;
            }
        } else {
            if (this.returnPortalBackgroundType === this._model?.return_portal_background_type) {
                return this._changes.return_portal_background_value ?? this._model?.return_portal_background_value;
            } else {
                return this._changes.return_portal_background_value;
            }
        }
        return undefined;
    }
    public get returnPortalBackgroundImageDimensions() { return this._return_portal_background_image_dimensions; }
    public get returnPortalBackgroundSize(): PORTAL_BACKGROUND_SIZE { return this._changes.return_portal_background_size ?? this._model?.return_portal_background_size ?? PORTAL_BACKGROUND_SIZE.STRETCH; }
    public get returnPortalForegroundColor(): string|undefined { return this._changes.return_portal_foreground_color ?? this._model?.return_portal_foreground_color; }
    public get returnPortalForegroundOpacity(): number|undefined { return this._changes.return_portal_foreground_opacity ?? this._model?.return_portal_foreground_opacity; }

    public get footerBackgroundColor(): string|undefined { return this._changes.footer_background_color ?? this._model?.footer_background_color; }
    public get headerBackgroundColor(): string|undefined { return this._changes.header_background_color ?? this._model?.header_background_color; }
    public get switchToExchangeLayoutType(): S2E_LAYOUT_TYPE|undefined { return this._changes.s2e_layout_type ?? this._model?.s2e_layout_type; }
    public get switchToExchangeLayoutData(): S2ELayoutItemStore[] { return this._s2e_layout_data; }
    public get switchToExchangeLayoutImages(): (string|undefined)[] { return this._s2e_layout_data.map((item) => item.image); }
    public get returnPortalCoverImage(): string | undefined {
        if (this._changes._return_portal_delete_cover_image) {
            return undefined;
        }
        if (this._changes._return_portal_new_cover_image) {
            return this._getNewBackgroundImageBase64Str(this._changes._return_portal_new_cover_image)
        }
        return this._changes.return_portal_cover_image ?? this._model?.return_portal_cover_image;
    }
    public get returnPortalCoverImageDimensions() { return this._return_portal_cover_image_dimensions; }

    public setInputTextColor(v: string) {
        this._changes.input_text_color = v;
        this._clearUnneededChanges();
    }

    public setInputBackgroundColor(v: string) {
        this._changes.input_background_color = v;
        this._clearUnneededChanges();
    }

    public setButtonBackgroundColor(v: string) {
        this._changes.button_background_color = v;
        this._clearUnneededChanges();
    }

    public setButtonTextColor(v: string) {
        this._changes.button_text_color = v;
        this._clearUnneededChanges();
    }

    public setTextColor(v: string) {
        this._changes.text_color = v;
        this._clearUnneededChanges();
    }

    public setTitleColor(v: string) {
        this._changes.title_color = v;
        this._clearUnneededChanges();
    }

    public setErrorColor(v: string) {
        this._changes.error_color = v;
        this._clearUnneededChanges();
    }

    public setSuccessColor(v: string) {
        this._changes.success_color = v;
        this._clearUnneededChanges();
    }

    public setPortalSecondaryBackgroundColor(v: string) {
        this._changes.portal_secondary_background_color = v;
        this._clearUnneededChanges();
    }

    public setPortalSecondaryBackgroundOpacity(v: number) {
        this._changes.portal_secondary_background_opacity = v;
        this._clearUnneededChanges();
    }

    public setStrokeColor(v: string) {
        this._changes.stroke_color = v;
        this._clearUnneededChanges();
    }

    public setPopupOverlayColor(v: string) {
        this._changes.popup_overlay_color = v;
        this._clearUnneededChanges();
    }

    public setPopupOverlayOpacity(v: number) {
        this._changes.popup_overlay_opacity = v;
        this._clearUnneededChanges();
    }

    public setDropShadowColor(v: string) {
        this._changes.drop_shadow_color = v;
        this._clearUnneededChanges();
    }

    public setDropShadowOpacity(v: number) {
        this._changes.drop_shadow_opacity = v;
        this._clearUnneededChanges();
    }

    public setPortalCss(v: string) {
        this._changes.portal_css = v;
        this._clearUnneededChanges();
    }

    public setPortalBorderRadius(v: number) {
        this._changes.portal_border_radius = v;
        this._clearUnneededChanges();
    }

    public setButtonBorderRadius(v: number) {
        this._changes.button_border_radius = v;
        this._clearUnneededChanges();
    }

    public setElementsBorderRadius(v: number) {
        this._changes.elements_border_radius = v;
        this._clearUnneededChanges();
    }

    public setShowCompareAtPriceStrikeThrough(v: boolean) {
        this._changes.show_compare_at_price_strike_through = v;
        this._clearUnneededChanges();
    }

    public setShowProductExchangePriceDifferenceStrikeThrough(v: boolean) {
        this._changes.show_product_exchange_price_difference_strike_through = v;
        this._clearUnneededChanges();
    }

    public setReturnPortalBackgroundType(v: PORTAL_BACKGROUND_TYPE) {
        this._changes.return_portal_background_type = v;
        if (this._changes.return_portal_background_type === this._model?.return_portal_background_type) {
            this._changes.return_portal_background_value = this._model?.return_portal_background_value;
        } else {
            if (v === PORTAL_BACKGROUND_TYPE.COLOR) {
                this._changes.return_portal_background_value = 'ffffff';
            } else {
                delete this._changes.return_portal_background_value;
            }
        }
        this._clearUnneededChanges();
    }

    public setReturnPortalBackgroundValue(v: string) {
        this._changes.return_portal_background_value = v;
        this._clearUnneededChanges();
    }

    public uploadReturnPortalBackgroundImage(type: string, base64_content: string) {
        this._changes._return_portal_new_background_image = {
            base64_content,
            type
        };
        delete this._changes._return_portal_delete_background_image;
        this._setReturnPortalBackgroundImageOriginalDimensions();
    }

    private _setReturnPortalBackgroundImageOriginalDimensions() {
        if (!this.returnPortalBackgroundValue) return;

        this._loadBackgroundImage(this.returnPortalBackgroundValue, (img: HTMLImageElement) => {
            this._return_portal_background_image_dimensions = img.width + 'x' + img.height;
        })
    }

    public deleteReturnPortalBackgroundImage() {
        this._changes._return_portal_delete_background_image = !!(this._model?.return_portal_background_type === PORTAL_BACKGROUND_TYPE.IMAGE && this._model?.return_portal_background_value);
        delete this._changes._return_portal_new_background_image;
    }

    public setReturnPortalBackgroundSize(v: PORTAL_BACKGROUND_SIZE) {
        this._changes.return_portal_background_size = v;
        this._clearUnneededChanges();
    }

    public setReturnPortalForegroundColor(v: string) {
        this._changes.return_portal_foreground_color = v;
        this._clearUnneededChanges();
    }

    public setReturnPortalForegroundOpacity(v: number) {
        this._changes.return_portal_foreground_opacity = v;
        this._clearUnneededChanges();
    }

    public setFooterBackgroundColor(v: string) {
        this._changes.footer_background_color = v;
        this._clearUnneededChanges();
    }

    public setHeaderBackgroundColor(v: string) {
        this._changes.header_background_color = v;
        this._clearUnneededChanges();
    }

    public setS2ELayoutType(v: S2E_LAYOUT_TYPE) {
        this._changes.s2e_layout_type = v;
        this._clearUnneededChanges();
    }

    public setSwitchToExchangeLayoutItemLink(index: number, link: string) {
        this._s2e_layout_data?.[index]?.setLink(link);
        this._s2e_layout_data = [ ...this._s2e_layout_data ];
    }

    public uploadSwitchToExchangeLayoutItemImage(index: number, type: string, base64_content: string) {
        this._s2e_layout_data?.[index]?.uploadImage(type, base64_content, () => {
            this._s2e_layout_data = [ ...this._s2e_layout_data ];
        });
    }

    public deleteSwitchToExchangeLayoutItemImage(index: number) {
        this._s2e_layout_data?.[index]?.deleteImage();
        this._s2e_layout_data = [ ...this._s2e_layout_data ];
    }

    public uploadReturnPortalCoverImage(type: string, base64_content: string) {
        this._changes._return_portal_new_cover_image = {
            base64_content,
            type
        };
        delete this._changes._return_portal_delete_cover_image;
        this._setReturnPortalCoverImageOriginalDimensions();
    }

    private _setReturnPortalCoverImageOriginalDimensions() {
        if (!this.returnPortalCoverImage) return;

        this._loadBackgroundImage(this.returnPortalCoverImage, (img: HTMLImageElement) => {
            this._return_portal_cover_image_dimensions = img.width + 'x' + img.height;
        })
    }

    public deleteReturnPortalCoverImage() {
        this._changes._return_portal_delete_cover_image = !!this._model?.return_portal_cover_image;
        delete this._changes._return_portal_new_cover_image;
    }

    public hasChanges(): boolean {
        return !!Object.keys(this._changes).length || this._s2e_layout_data.some((item) => item.hasChanges());
    }

    public getUpdatedModel(): IEdgeCustomization | undefined {
        if (!this._model) {
            return undefined;
        }

        const updatedModel = {
            ...this._model,
            ...this._changes,
            s2e_layout_data: this._s2e_layout_data.map((item) => item.getUpdatedModel())
        };
        return updatedModel;
    }

    public discardChanges(): void {
        this._changes = {} as IEdgeCustomizationChanges;
    }

    private _loadBackgroundImage(src: string, callback: (image: HTMLImageElement) => void) {
        const img = new Image();
        img.src = src;
        img.onerror = (err) => {
            console.error(err);
        };
        img.onload = () => {
            runInAction(() => callback(img));
        };
    }

    private _getNewBackgroundImageBase64Str(data?: INewBackgroundImageData): string | undefined {
        if (data) {
            return `data:${data.type};base64,${data.base64_content}`;
        }
        return undefined;
    }

    private _clearUnneededChanges() {
        Object.keys(this._changes).forEach(key => {
            const theKey = key as keyof IEdgeCustomization;
            if (this._changes[theKey] === this._model?.[theKey]) {
                delete this._changes[theKey];
            }
        });
    }

    public async load(): Promise<IEdgeCustomization> {
        const customizations = await this.service.loadEdgeCustomizations();
        this.setModel(customizations);
        return customizations;
    }

    public async save(fontOverlay?: Partial<IEdgeCustomization>): Promise<void> {
        const updatedModel = this.getUpdatedModel();

        // model is not loaded yet, we can't update it
        if (!updatedModel) {
            return;
        }

        this._isSaving = true;

        try {
            const merged: IEdgeCustomization = fontOverlay
                ? ({ ...updatedModel, ...fontOverlay } as IEdgeCustomization)
                : updatedModel;
            const updatedModelWithOld: WithOld<IEdgeCustomization> = merged;
            updatedModelWithOld.__old = this._model;
            const responseCustomizations = await this.service.updateEdgeCustomizations(updatedModelWithOld);
            runInAction(() => {
                this.setModel(responseCustomizations);
                this._isSaving = false;
            });
        } catch (error) {
            runInAction(() => {
                this._isSaving = false;
            });
        }
    }
}

export default CustomizationEdgeStore;
